import React from 'react';
import { Input } from './ui/input';
import MaskedInput from './ui/MaskedInput';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BusinessStep = ({ formData, setFormData }) => {
  const updateCompany = (field, value) => {
    setFormData(prev => ({
      ...prev,
      company: { ...prev.company, [field]: value }
    }));
  };

  const updateCompanyAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      company: {
        ...prev.company,
        address: { ...prev.company?.address, [field]: value }
      }
    }));
  };

  const updateDBA = (field, value) => {
    setFormData(prev => ({
      ...prev,
      dba: { ...prev.dba, [field]: value }
    }));
  };

  const updateBusinessLocation = (field, value) => {
    setFormData(prev => ({
      ...prev,
      businessLocation: { ...prev.businessLocation, [field]: value }
    }));
  };

  const updateCorporateContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      corporateContact: { ...prev.corporateContact, [field]: value }
    }));
  };

  const updateCustomerServiceContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customerServiceContact: { ...prev.customerServiceContact, [field]: value }
    }));
  };

  return (
    <div className="space-y-8">
      {/* Business Information Section */}
      <div className="bg-slate-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-slate-100 mb-6">Business Information</h3>
        <p className="text-slate-300 mb-6">We need to know a little about your business, please complete the following.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="companyName" className="text-slate-200">Corporate / Legal Name *</Label>
            <Input
              id="companyName"
              data-testid="company-name-input"
              value={formData.company?.name || ''}
              onChange={(e) => updateCompany('name', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="companyType" className="text-slate-200">Ownership / Entity Type *</Label>
            <Select value={formData.company?.type || ''} onValueChange={(value) => updateCompany('type', value)}>
              <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100" data-testid="company-type-select">
                <SelectValue placeholder="-- Choose --" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Individual">Individual</SelectItem>
                <SelectItem value="Partnership">Partnership</SelectItem>
                <SelectItem value="Corporation">Corporation</SelectItem>
                <SelectItem value="LLC">LLC</SelectItem>
                <SelectItem value="Non-Profit">Non-Profit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="md:col-span-2">
            <Label htmlFor="streetAddress" className="text-slate-200">Street Address *</Label>
            <Input
              id="streetAddress"
              data-testid="street-address-input"
              value={formData.company?.address?.street || ''}
              onChange={(e) => updateCompanyAddress('street', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="state" className="text-slate-200">State *</Label>
            <Input
              id="state"
              data-testid="state-input"
              value={formData.company?.address?.state || ''}
              onChange={(e) => updateCompanyAddress('state', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              placeholder="e.g., California"
            />
          </div>
          
          <div>
            <Label htmlFor="zip" className="text-slate-200">ZIP *</Label>
            <Input
              id="zip"
              data-testid="zip-input"
              value={formData.company?.address?.zip || ''}
              onChange={(e) => updateCompanyAddress('zip', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="city" className="text-slate-200">City *</Label>
            <Input
              id="city"
              data-testid="city-input"
              value={formData.company?.address?.city || ''}
              onChange={(e) => updateCompanyAddress('city', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="founded" className="text-slate-200">When was your business founded? *</Label>
            <Input
              id="founded"
              type="date"
              data-testid="founded-input"
              value={formData.company?.founded || ''}
              onChange={(e) => updateCompany('founded', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="federalTaxId" className="text-slate-200">Federal Tax ID / EIN *</Label>
            <MaskedInput
              id="federalTaxId"
              data-testid="federal-tax-id-input"
              value={formData.company?.federalTaxId || ''}
              onChange={(e) => updateCompany('federalTaxId', e.target.value)}
              maskPattern="ein"
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              placeholder="XX-XXXXXXX"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <Label className="text-slate-200">Is your DBA (Doing Business As) information same as Corporate/Legal? *</Label>
          <div className="mt-3 space-y-2">
            <div className="flex items-center">
              <input
                type="radio"
                id="dbaYes"
                name="dbaSame"
                value="Yes"
                checked={formData.dba?.sameAsCompany === 'Yes'}
                onChange={(e) => updateDBA('sameAsCompany', e.target.value)}
                className="mr-2"
              />
              <Label htmlFor="dbaYes" className="text-slate-300 cursor-pointer">Yes</Label>
            </div>
            <div className="flex items-center">
              <input
                type="radio"
                id="dbaNo"
                name="dbaSame"
                value="No"
                checked={formData.dba?.sameAsCompany === 'No'}
                onChange={(e) => updateDBA('sameAsCompany', e.target.value)}
                className="mr-2"
              />
              <Label htmlFor="dbaNo" className="text-slate-300 cursor-pointer">No</Label>
            </div>
          </div>
        </div>
      </div>

      {/* Business Location Section */}
      <div className="bg-slate-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-slate-100 mb-6">Business Location</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <Label htmlFor="buildingType" className="text-slate-200">Building Type *</Label>
            <Select value={formData.businessLocation?.buildingType || ''} onValueChange={(value) => updateBusinessLocation('buildingType', value)}>
              <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100" data-testid="building-type-select">
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Shopping Center">Shopping Center</SelectItem>
                <SelectItem value="Office Building">Office Building</SelectItem>
                <SelectItem value="Industrial Building">Industrial Building</SelectItem>
                <SelectItem value="Residence">Residence</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="buildingOwnership" className="text-slate-200">Building Ownership *</Label>
            <Select value={formData.businessLocation?.buildingOwnership || ''} onValueChange={(value) => updateBusinessLocation('buildingOwnership', value)}>
              <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100" data-testid="building-ownership-select">
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Owns">Owns</SelectItem>
                <SelectItem value="Rents">Rents</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="areaZoned" className="text-slate-200">Area Zoned *</Label>
            <Select value={formData.businessLocation?.areaZoned || ''} onValueChange={(value) => updateBusinessLocation('areaZoned', value)}>
              <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100" data-testid="area-zoned-select">
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Industrial">Industrial</SelectItem>
                <SelectItem value="Residential">Residential</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="squareFootage" className="text-slate-200">Square Footage *</Label>
            <Select value={formData.businessLocation?.squareFootage || ''} onValueChange={(value) => updateBusinessLocation('squareFootage', value)}>
              <SelectTrigger className="mt-2 bg-slate-900 border-slate-700 text-slate-100" data-testid="square-footage-select">
                <SelectValue placeholder="Choose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0-500">0-500</SelectItem>
                <SelectItem value="501-2500">501-2500</SelectItem>
                <SelectItem value="2501-5000">2501-5000</SelectItem>
                <SelectItem value="5001-10000">5001-10000</SelectItem>
                <SelectItem value="10000+">10000+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Corporate Contact Information Section */}
      <div className="bg-slate-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold text-slate-100 mb-6">Corporate Contact Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="businessPhone" className="text-slate-200">Business Phone *</Label>
            <Input
              id="businessPhone"
              type="tel"
              data-testid="business-phone-input"
              value={formData.customerServiceContact?.phone || ''}
              onChange={(e) => updateCustomerServiceContact('phone', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              placeholder="+1"
            />
          </div>
          
          <div>
            <Label htmlFor="businessEmail" className="text-slate-200">Business Email</Label>
            <Input
              id="businessEmail"
              type="email"
              data-testid="business-email-input"
              value={formData.customerServiceContact?.email || ''}
              onChange={(e) => updateCustomerServiceContact('email', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
          
          <div>
            <Label htmlFor="contactPhone" className="text-slate-200">Contact Phone</Label>
            <Input
              id="contactPhone"
              type="tel"
              data-testid="contact-phone-input"
              value={formData.corporateContact?.phone || ''}
              onChange={(e) => updateCorporateContact('phone', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              placeholder="+1"
            />
          </div>
          
          <div>
            <Label htmlFor="faxNumber" className="text-slate-200">FAX Number</Label>
            <Input
              id="faxNumber"
              type="tel"
              data-testid="fax-number-input"
              value={formData.corporateContact?.fax || ''}
              onChange={(e) => updateCorporateContact('fax', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
              placeholder="+1"
            />
          </div>
          
          <div>
            <Label htmlFor="contactEmail" className="text-slate-200">Contact Email</Label>
            <Input
              id="contactEmail"
              type="email"
              data-testid="contact-email-input"
              value={formData.corporateContact?.email || ''}
              onChange={(e) => updateCorporateContact('email', e.target.value)}
              className="mt-2 bg-slate-900 border-slate-700 text-slate-100"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessStep;
